// Org settings for join requests: the claimed email domain, the auto-approve switch, the
// trial-seat pool (super admin only), the Billing Owner, and each Trainer's approval right.
//
// One card rather than four, because the four settings only make sense together: claiming
// a domain is what turns the feature on, auto-approve decides whether anyone is asked, and
// the Billing Owner plus the approval switches decide who does the asking. Splitting them
// would let an admin claim a domain and never notice that nobody can act on what arrives.
import React, { useCallback, useEffect, useState } from 'react';
import { Crown, Globe, ShieldCheck } from 'lucide-react';
import { T } from '../gamification/shared';
import ConfirmationModal from '../shared/ConfirmationModal';
import { createLogger } from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import {
    Approver,
    JoinSettings,
    fetchApprovers,
    fetchJoinSettings,
    setApprovalRights,
    updateJoinSettings,
} from '../../services/joinRequestService';

const logger = createLogger('JoinSettingsCard');

interface Props {
    organizationId: string;
    /** Gates the trial-seat field. The API enforces this too — this only avoids showing
     *  an admin a control that would 403 the moment they used it. */
    isSuperAdmin?: boolean;
}

const inputStyle: React.CSSProperties = {
    padding: '8px 10px',
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    fontSize: '0.85rem',
    fontFamily: T.font,
    color: T.text,
    boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: T.muted,
    marginBottom: 6,
    letterSpacing: '0.02em',
};

// Section-level headings ("Auto-approve", "Trial seats", "WHO CAN APPROVE") read as body
// text at labelStyle's size — this is the bigger, bolder treatment for the ones that head
// a whole block rather than a single field, so the hierarchy in the card is visible at a
// glance instead of every heading blending into its own description.
const sectionHeadingStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: 700,
    color: T.text,
};

const hintStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    // T.faint reads as near-invisible for a full sentence of explanatory copy — fine for a
    // one-word placeholder, not for the descriptions every field in this card relies on.
    color: T.muted,
    lineHeight: 1.6,
    margin: '6px 0 0',
};

const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 0',
    borderBottom: `1px solid ${T.border}`,
};

const JoinSettingsCard: React.FC<Props> = ({ organizationId, isSuperAdmin }) => {
    const { currentUser } = useAuth();
    const [settings, setSettings] = useState<JoinSettings | null>(null);
    const [approvers, setApprovers] = useState<Approver[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [confirmAutoApprove, setConfirmAutoApprove] = useState(false);
    // Which approver row is mid-request, so only that row's controls disable —
    // `saving` alone would greyscale every row in the roster for one person's toggle.
    const [busyApproverId, setBusyApproverId] = useState<string | null>(null);

    // Only the Billing Owner and whoever already has approval rights are shown by
    // default — the org can have dozens of Trainers, and most of them will never
    // approve anything. Anyone else is found through the search box below instead
    // of scrolling a long, mostly-irrelevant roster.
    const [approverSearch, setApproverSearch] = useState('');

    // Draft values, so typing a domain doesn't fire a request per keystroke.
    const [domainDraft, setDomainDraft] = useState('');
    const [seatsDraft, setSeatsDraft] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [loaded, staff] = await Promise.all([
                fetchJoinSettings(organizationId),
                // Best-effort: a Keycloak hiccup should cost the roster, not the whole card.
                fetchApprovers(organizationId).catch(err => {
                    logger.warn('Could not load approvers', err);
                    return [] as Approver[];
                }),
            ]);
            setSettings(loaded);
            setApprovers(staff);
            setDomainDraft(loaded.domain ?? '');
            setSeatsDraft(String(loaded.default_trial_seats));
        } catch (err) {
            logger.error('Failed to load join settings', err);
            setError('Could not load join settings.');
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        load();
    }, [load]);

    const runWithBusy = async (
        setBusy: (busy: boolean) => void,
        work: () => Promise<unknown>,
        success: string
    ): Promise<boolean> => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await work();
            await load();
            setNotice(success);
            return true;
        } catch (err) {
            // The API's messages are the specific ones — "that domain is already claimed",
            // "gmail.com is a public email provider", "the Billing Owner always has
            // approval rights" — and each names the fix.
            const message = err instanceof Error ? err.message : 'That didn’t save. Please try again.';
            logger.warn('Join settings update failed', err);
            setError(message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const run = (work: () => Promise<unknown>, success: string): Promise<boolean> =>
        runWithBusy(setSaving, work, success);

    // Approver-row actions (search-add, "Make owner", the "Can approve" checkbox) key
    // their busy state to the one person being changed, so the rest of the roster stays
    // interactive instead of greying out for someone else's toggle.
    const runForApprover = (userId: string, work: () => Promise<unknown>, success: string): Promise<boolean> =>
        runWithBusy(busy => setBusyApproverId(busy ? userId : null), work, success);

    // Only show the full placeholder before the first fetch resolves. `run()`
    // re-triggers `load()` after every save (toggling auto-approve, saving the
    // domain, etc.), which flips `loading` back to true — without this guard
    // the whole card, including the checkbox the user just clicked, would
    // blank out to this placeholder on every single toggle even though the
    // previous settings are still valid to show while the refresh happens in
    // the background (inputs are already disabled via `saving` during that
    // window).
    if (loading && !settings) {
        return (
            <div style={{ padding: 16, fontFamily: T.font, color: T.faint, fontSize: '0.85rem' }}>
                Loading join settings…
            </div>
        );
    }

    if (!settings) {
        return (
            <div style={{ padding: 16, fontFamily: T.font }}>
                <p style={{ color: '#991b1b', fontSize: '0.85rem' }}>{error ?? 'Join settings unavailable.'}</p>
            </div>
        );
    }

    const domainChanged = (domainDraft.trim() || null) !== (settings.domain ?? null);
    const seatsChanged = seatsDraft !== String(settings.default_trial_seats);

    // Claiming/releasing the domain and toggling auto-approve are gated to the Billing
    // Owner (or a super admin) — the API enforces this too, so this only avoids
    // showing a trainer a control that would 403 the moment they used it. See
    // _require_billing_owner on PATCH /join-settings.
    const isBillingOwnerOrSA = !!isSuperAdmin || (!!currentUser?.id && currentUser.id === settings.billing_owner_id);

    const visibleApprovers = approvers.filter(person => person.is_billing_owner || person.can_approve);

    const approverSearchQuery = approverSearch.trim().toLowerCase();
    const approverSearchResults = approverSearchQuery
        ? approvers
              .filter(person => !person.is_billing_owner && !person.can_approve)
              .filter(
                  person =>
                      person.name.toLowerCase().includes(approverSearchQuery) ||
                      person.email.toLowerCase().includes(approverSearchQuery)
              )
              .slice(0, 6)
        : [];

    return (
        <div
            style={{
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                boxShadow: T.shadow,
                padding: 20,
                fontFamily: T.font,
            }}
        >
            <h3
                style={{
                    margin: '0 0 4px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: T.text,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <ShieldCheck size={17} style={{ color: T.primary }} /> Join requests
            </h3>
            <p style={{ ...hintStyle, marginBottom: 18 }}>
                Controls what happens when someone signs up with an email at this organization’s domain.
            </p>

            {error && (
                <div
                    style={{
                        background: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderRadius: 8,
                        padding: '9px 12px',
                        marginBottom: 14,
                        fontSize: '0.8rem',
                        color: '#991b1b',
                        lineHeight: 1.5,
                    }}
                >
                    {error}
                </div>
            )}
            {notice && (
                <div
                    style={{
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 8,
                        padding: '9px 12px',
                        marginBottom: 14,
                        fontSize: '0.8rem',
                        color: '#14532d',
                    }}
                >
                    {notice}
                </div>
            )}

            {/* ── Domain ── */}
            <div style={{ marginBottom: 20 }}>
                <label style={labelStyle} htmlFor="join-domain">
                    <Globe size={12} style={{ verticalAlign: -2, marginRight: 5 }} />
                    EMAIL DOMAIN
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                        id="join-domain"
                        className="survey-input"
                        value={domainDraft}
                        placeholder="elastiq.com"
                        disabled={!isBillingOwnerOrSA}
                        onChange={e => setDomainDraft(e.target.value)}
                        style={{ ...inputStyle, flex: 1, minWidth: 180 }}
                    />
                    <button
                        disabled={saving || !domainChanged || !isBillingOwnerOrSA}
                        onClick={() =>
                            run(
                                () => updateJoinSettings(organizationId, { domain: domainDraft.trim() }),
                                domainDraft.trim() ? 'Domain saved.' : 'Domain cleared.'
                            )
                        }
                        style={{
                            padding: '8px 16px',
                            borderRadius: 8,
                            border: 'none',
                            background: T.primary,
                            color: '#fff',
                            fontFamily: T.font,
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: saving || !domainChanged || !isBillingOwnerOrSA ? 'not-allowed' : 'pointer',
                            opacity: saving || !domainChanged || !isBillingOwnerOrSA ? 0.5 : 1,
                        }}
                    >
                        Save
                    </button>
                </div>
                <p style={hintStyle}>
                    Signups from this domain go through the approval flow below. Leave it empty to
                    turn that off entirely. Public providers like gmail.com can’t be claimed — one
                    organization would end up intercepting every unrelated signup.
                    {!isBillingOwnerOrSA && ' Only the Billing Owner can change this.'}
                </p>
            </div>

            {/* ── Auto-approve ── */}
            <div style={{ ...rowStyle, borderTop: `1px solid ${T.border}` }}>
                <div style={{ flex: 1 }}>
                    <div style={sectionHeadingStyle}>Auto-approve</div>
                    <p style={hintStyle}>
                        Skip approval entirely — anyone with a matching email domain joins immediately.
                        A domain match only proves who owns their inbox, not that you want them in, so
                        this is off by default.
                    </p>
                </div>
                <input
                    type="checkbox"
                    checked={settings.auto_approve_join_requests}
                    disabled={saving || !isBillingOwnerOrSA}
                    title={!isBillingOwnerOrSA ? 'Only the Billing Owner can change this.' : undefined}
                    onChange={e => {
                        // Only confirm turning it on — that's the direction that starts
                        // letting people in without a human looking, which is the risky
                        // one. Turning it back off is always safe to apply immediately.
                        if (e.target.checked) {
                            setConfirmAutoApprove(true);
                            return;
                        }
                        run(
                            () => updateJoinSettings(organizationId, { auto_approve_join_requests: false }),
                            'Auto-approve is off.'
                        );
                    }}
                    style={{
                        width: 18,
                        height: 18,
                        cursor: saving || !isBillingOwnerOrSA ? 'not-allowed' : 'pointer',
                        opacity: isBillingOwnerOrSA ? 1 : 0.5,
                    }}
                />
            </div>

            {/* ── Trial seat pool (super admin only) ── */}
            {isSuperAdmin && (
                <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <div style={sectionHeadingStyle}>
                            Trial seats
                        </div>
                        <p style={hintStyle}>
                            How many approved applicants can wait on Trial Mode at once before a seat
                            has to be freed. Platform-level setting — organization admins can’t change it.
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            type="number"
                            min={0}
                            value={seatsDraft}
                            onChange={e => setSeatsDraft(e.target.value)}
                            style={{ ...inputStyle, width: 80 }}
                        />
                        <button
                            disabled={saving || !seatsChanged}
                            onClick={() =>
                                run(
                                    () =>
                                        updateJoinSettings(organizationId, {
                                            default_trial_seats: Number(seatsDraft),
                                        }),
                                    'Trial seat allowance saved.'
                                )
                            }
                            style={{
                                padding: '8px 14px',
                                borderRadius: 8,
                                border: `1px solid ${T.border}`,
                                background: T.card,
                                color: T.muted,
                                fontFamily: T.font,
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                cursor: saving || !seatsChanged ? 'not-allowed' : 'pointer',
                                opacity: saving || !seatsChanged ? 0.5 : 1,
                            }}
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}

            {/* ── Billing Owner + approval rights ── */}
            <div style={{ marginTop: 20 }}>
                <div style={{ ...sectionHeadingStyle, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Crown size={15} style={{ color: T.muted }} />
                    Who can approve
                </div>
                <p style={{ ...hintStyle, marginTop: 6, marginBottom: 10 }}>
                    Only the Billing Owner is emailed about new requests; everyone else with approval
                    rights gets an in-app notification for the same request, at the same time. The
                    Billing Owner always keeps approval rights — transfer the role before switching
                    someone off.
                </p>

                <div style={{ position: 'relative', marginBottom: 14 }}>
                    <input
                        className="survey-input"
                        value={approverSearch}
                        onChange={e => setApproverSearch(e.target.value)}
                        placeholder="Search Trainers or Admins to give them approval rights…"
                        style={{ ...inputStyle, width: '100%' }}
                    />
                    {approverSearchQuery && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: 4,
                                background: T.card,
                                border: `1px solid ${T.border}`,
                                borderRadius: 8,
                                boxShadow: T.shadow,
                                zIndex: 1,
                                maxHeight: 220,
                                overflowY: 'auto',
                            }}
                        >
                            {approverSearchResults.length === 0 ? (
                                <p style={{ ...hintStyle, padding: '10px 12px', margin: 0, fontStyle: 'italic' }}>
                                    No matching Trainer or Admin without approval rights.
                                </p>
                            ) : (
                                approverSearchResults.map(person => (
                                    <div
                                        key={person.user_id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 10,
                                            padding: '8px 12px',
                                            borderBottom: `1px solid ${T.border}`,
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    color: T.text,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {person.name || person.email}
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: T.muted }}>
                                                {person.email} · {person.role}
                                            </div>
                                        </div>
                                        <button
                                            disabled={busyApproverId === person.user_id}
                                            onClick={async () => {
                                                const success = await runForApprover(
                                                    person.user_id,
                                                    () => setApprovalRights(organizationId, person.user_id, true),
                                                    `${person.name || person.email} can approve requests.`
                                                );
                                                if (success) setApproverSearch('');
                                            }}
                                            style={{
                                                padding: '5px 11px',
                                                borderRadius: 8,
                                                border: 'none',
                                                background: T.primary,
                                                color: '#fff',
                                                fontFamily: T.font,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                cursor: saving ? 'not-allowed' : 'pointer',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Add
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {visibleApprovers.length === 0 ? (
                    <p style={{ ...hintStyle, fontStyle: 'italic' }}>
                        Nobody besides the Billing Owner can approve requests yet. Search above to add someone.
                    </p>
                ) : (
                    visibleApprovers.map(person => (
                        <div key={person.user_id} style={rowStyle}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: T.text,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 7,
                                    }}
                                >
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {person.name || person.email}
                                    </span>
                                    {person.is_billing_owner && (
                                        <span
                                            style={{
                                                background: '#fef3c7',
                                                color: '#92400e',
                                                borderRadius: 999,
                                                padding: '1px 8px',
                                                fontSize: '0.68rem',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            BILLING OWNER
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: T.muted, marginTop: 2 }}>
                                    {person.email} · {person.role}
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {/* "Make owner" is pulled pending a separate discussion of what a billing-owner
                                    transfer should actually move (Stripe subscription stays with the original
                                    purchaser today — only org-level role/approval routing changes). Backend
                                    endpoint (POST /organization/{id}/billing-owner) is untouched. */}
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontSize: '0.75rem',
                                        color: T.muted,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={person.can_approve}
                                        // The Billing Owner's switch is locked on: the role exists so
                                        // there is always someone who can act, and the server refuses
                                        // to turn it off for exactly that reason.
                                        disabled={busyApproverId === person.user_id || person.is_billing_owner}
                                        onChange={e =>
                                            runForApprover(
                                                person.user_id,
                                                () =>
                                                    setApprovalRights(
                                                        organizationId,
                                                        person.user_id,
                                                        e.target.checked
                                                    ),
                                                e.target.checked
                                                    ? `${person.name || person.email} can approve requests.`
                                                    : `${person.name || person.email} can no longer approve requests.`
                                            )
                                        }
                                        style={{ width: 16, height: 16 }}
                                    />
                                    Can approve
                                </label>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmationModal
                isOpen={confirmAutoApprove}
                title="Turn on auto-approve?"
                message="Anyone who signs up with an email at your claimed domain will join immediately, with no approval step."
                confirmText="Turn on"
                cancelText="Cancel"
                loading={saving}
                onConfirm={() => {
                    setConfirmAutoApprove(false);
                    run(
                        () => updateJoinSettings(organizationId, { auto_approve_join_requests: true }),
                        'Auto-approve is on.'
                    );
                }}
                onCancel={() => setConfirmAutoApprove(false)}
            />
        </div>
    );
};

export default JoinSettingsCard;
